/**
 * Privacy-safe wrappers around ``Sentry.captureMessage`` and
 * ``Sentry.captureException`` for non-crashing product failures.
 *
 * Why this exists: the ``RootErrorBoundary`` catches synchronous render
 * crashes, but real-user failures in Bosun (auth hangs, chat failures,
 * preview render loops, AI fill stream errors) NEVER reach a React error
 * boundary — they're awaited promises and SSE callbacks. Without explicit
 * capture calls, Sentry only sees the symptomatic crash long after the
 * actual error path that caused it. These helpers give us a single place
 * to surface those handled failures with stable tags / scrubbed extras.
 *
 * Privacy hard rule (mirrors ``sentry-scrub.ts``): never send raw project
 * context, task output, file bytes, workspace_context, auth tokens, emails, etc. Every
 * field value passed to the helpers below runs through :func:`scrubValue`
 * BEFORE it hits Sentry's scope — so even a careless
 * ``captureSafeMessage('foo', 'warning', { body: userText })`` cannot leak.
 * The ``beforeSend`` hook in ``main.tsx`` is a second line of defense.
 */

import * as Sentry from '@sentry/react'
import type { SeverityLevel } from '@sentry/react'

import { isSensitiveKey, REDACTED, scrubValue } from './sentry-scrub'

/** Stable subset of field keys we lift onto Sentry as tags so the issue
 *  list is filterable. Anything else lands under the ``log_event``
 *  Sentry context object instead — tag keys are bounded and indexed, so
 *  spamming arbitrary keys there breaks the dashboard. */
const TAG_KEYS = new Set([
  'event',
  'environment',
  'operation',
  'release',
  'route',
  'service',
  'reason',
  'status',
  'status_code',
  'transaction',
  'mode',
  'project_uuid',
  'workspace_uuid',
  'run_id',
  'retry_count',
  'retries',
])

const MAX_TAG_LEN = 200

export interface SafeFields {
  [key: string]: unknown
}

function scrubFields(fields: SafeFields): SafeFields {
  const out: SafeFields = {}
  for (const [key, value] of Object.entries(fields)) {
    out[key] = isSensitiveKey(key) ? REDACTED : scrubValue(value)
  }
  return out
}

function tagValue(value: unknown): string | null {
  if (typeof value === 'string') return value.slice(0, MAX_TAG_LEN)
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return null
}

/**
 * Capture a non-crashing failure / hang signal to Sentry as a message-level
 * event. ``event`` is a short dotted identifier matching the backend
 * ``log_event`` taxonomy (``auth.callback_hang``, ``run.start_failed``,
 * ``deployment.fetch_failed``, ``provisioning.stream_failed``, ...) so on-call can
 * pivot between the two halves of the stack on one tag.
 *
 * No-op when Sentry isn't initialized.
 *
 * @example
 *   captureSafeMessage('run.start_failed', 'error', {
 *     mode: 'provision',
 *     status: 502,
 *     project_uuid: project.uuid,
 *   })
 */
export function captureSafeMessage(
  event: string,
  level: SeverityLevel,
  fields: SafeFields = {},
): void {
  try {
    const client = Sentry.getClient()
    if (!client) return
    const scrubbed = scrubFields(fields)
    Sentry.withScope((scope) => {
      scope.setLevel(level)
      scope.setTag('event', event)
      for (const [key, value] of Object.entries(scrubbed)) {
        if (!TAG_KEYS.has(key)) continue
        const tv = tagValue(value)
        if (tv !== null) scope.setTag(key, tv)
      }
      scope.setContext('log_event', { event, ...scrubbed })
      Sentry.captureMessage(`${event} (${level})`, level)
    })
  } catch {
    // Sentry-side bugs (e.g. tag value too long, missing client) must never
    // surface as a second crash in the original failure path. Swallow.
  }
}

/**
 * Capture a non-crashing exception to Sentry. Wraps the exception with
 * scrubbed tags / extras so an on-call engineer can pivot from a Sentry
 * issue to the matching log line via ``event`` + ``request_id``.
 *
 * Prefer ``captureSafeException`` when you HAVE an Error object (network
 * fetch reject, SSE stream onError, provisioning stream error event); fall back
 * to ``captureSafeMessage`` for hang detectors / structured signals where
 * no exception object exists.
 *
 * @example
 *   captureSafeException(err, 'fill.stream_failed', {
 *     route: '/dashboard/workspaces/{uuid}/bosun/projects',
 *     project_uuid: project.uuid,
 *   })
 */
export function captureSafeException(
  err: unknown,
  event: string,
  fields: SafeFields = {},
): void {
  try {
    const client = Sentry.getClient()
    if (!client) return
    const scrubbed = scrubFields(fields)
    Sentry.withScope((scope) => {
      scope.setLevel('error')
      scope.setTag('event', event)
      for (const [key, value] of Object.entries(scrubbed)) {
        if (!TAG_KEYS.has(key)) continue
        const tv = tagValue(value)
        if (tv !== null) scope.setTag(key, tv)
      }
      scope.setContext('log_event', { event, ...scrubbed })
      if (err instanceof Error) {
        Sentry.captureException(err)
      } else {
        Sentry.captureException(new Error(`${event}: ${typeof err}`))
      }
    })
  } catch {
    // see captureSafeMessage for rationale.
  }
}
